import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import ArtLayer from "../art/ArtLayer";
import { DoorFrame, DoorLeaf } from "../art/Doors";
import { coupleNames } from "../helpers";
import { takeBirdHandoff } from "../art/birdHandoff";
import { prefersReducedMotion } from "../hooks";
import GateBird from "./GateBird";
import SacredMark from "./SacredMark";

// Doors swing open, then light floods out, then the gate dissolves into the card.
const DOORS_MS = 1700;
const LIGHT_MS = 650;
const FADE_MS = 800;

/**
 * First screen of the invitation: the palace gate, closed, in the same scenery
 * as the card. "Open Invitation" swings the doors open and reveals the card.
 */
const GateScreen = ({ bride, groom, onReveal, onDone }) => {
  const [stage, setStage] = useState("closed");
  // Where the loader's bird and caption were. The loader records this as it unmounts, which
  // happens after this first render but before the first paint, so it is read in a layout effect.
  const [from, setFrom] = useState(null);
  useLayoutEffect(() => setFrom(takeBirdHandoff()), []);
  const timers = useRef([]);
  const { first, second } = coupleNames(bride, groom);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const open = () => {
    if (stage !== "closed") return;
    // Start the music inside the tap itself so mobile browsers allow it.
    const music = document.getElementById("backgroundMusic");
    music?.play?.()?.catch?.(() => { });

    if (prefersReducedMotion()) {
      onReveal();
      setStage("leaving");
      later(onDone, FADE_MS);
      return;
    }
    setStage("opening");
    later(() => setStage("entering"), DOORS_MS);
    later(() => {
      onReveal();
      setStage("leaving");
    }, DOORS_MS + LIGHT_MS);
    later(onDone, DOORS_MS + LIGHT_MS + FADE_MS);
  };

  return (
    <div className={`rm-gate is-${stage}`} role="dialog" aria-modal="true" aria-label="Wedding invitation">
      {from?.text && (
        <p
          className="rm-loader__text rm-gate__farewell"
          style={{ left: from.text.left, top: from.text.top }}
          aria-hidden="true"
        >
          {from.text.label}
        </p>
      )}
      <GateBird from={from} />

      <div className="rm-gate__intro">
        <SacredMark className="rm-hero__emblem" />
        <p className="rm-kicker">A royal invitation for you</p>
        <p className="rm-gate__names">
          {first} <span className="rm-script">&amp;</span> {second}
        </p>
      </div>

      <div className="rm-gate__doorway">
        <div className="rm-gate__glow" aria-hidden="true" />
        <div className="rm-gate__leaves" aria-hidden="true">
          <div className="rm-gate__leaf rm-gate__leaf--left">
            <ArtLayer slot="doorLeft">
              <DoorLeaf side="left" />
            </ArtLayer>
          </div>
          <div className="rm-gate__leaf rm-gate__leaf--right">
            <ArtLayer slot="doorRight">
              <DoorLeaf side="right" />
            </ArtLayer>
          </div>
        </div>
        <ArtLayer slot="doorFrame">
          <DoorFrame className="rm-gate__frame" />
        </ArtLayer>
        {/* A brass knob at the seam where the doors meet */}
        <button type="button" className="rm-open-btn" onClick={open} disabled={stage !== "closed"} aria-label="Open Invitation">
          <span className="rm-open-btn__face">Open</span>
          <span className="rm-open-btn__hint" aria-hidden="true">
            Tap to open
          </span>
        </button>
      </div>

      <div className="rm-gate__light" aria-hidden="true" />
    </div>
  );
};

const mapStateToProps = (state) => ({
  bride: state.inviteDetails.invite?.bride,
  groom: state.inviteDetails.invite?.groom,
});

export default connect(mapStateToProps)(GateScreen);
